/** @file Weighted bones whose bind pose no longer equals their setup world (a stale bind, §6.7). */
import type { Id } from '@pose2d/format/types';

/** Largest difference per matrix entry, relative to the entry's size (at least 1), still read as the same matrix. */
const TOLERANCE = 1e-6;

/** True when the 2x3 affines agree entry by entry within TOLERANCE·max(1, |a|, |b|). */
function sameMatrix(a: ArrayLike<number>, b: readonly number[]): boolean {
  for (let i = 0; i < 6; i++) {
    const x = a[i] ?? Number.NaN;
    const y = b[i] ?? Number.NaN;
    if (!(Math.abs(x - y) <= TOLERANCE * Math.max(1, Math.abs(x), Math.abs(y)))) return false;
  }
  return true;
}

/**
 * The bones of `bones` (every bindPose key by default) whose bindPose entry differs from `worldOf(bone)`, each once, in
 * ascending id order; a bone without a bindPose entry is never stale. Weights written for a stale bone move vertices at
 * the setup pose (W·B⁻¹ ≠ I); mesh.rebindMesh re-captures every bind at setup and keeps the setup look.
 */
export function staleBindBones(bindPose: Readonly<Record<Id, readonly number[]>>, worldOf: (bone: Id) => ArrayLike<number>, bones: Iterable<Id> = Object.keys(bindPose)): Id[] {
  return [...new Set(bones)].filter((bone) => {
    const bind = bindPose[bone];
    return bind !== undefined && !sameMatrix(worldOf(bone), bind);
  }).sort();
}
